import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PageShell } from '@/components/Layout';
import { Device } from '@/lib/device';
import { fetchDashboardSummary } from '@/api/endpoints';

export default function Dashboard({ device }: { device: Device }) {
  const { data: summary, isLoading, isError, error } = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: fetchDashboardSummary,
    refetchInterval: 15_000,
  });

  if (isError) {
    return (
      <PageShell device={device} title="Dashboard" breadcrumb="Principal">
        <div className="p-8 text-center">
          <p className="text-destructive font-medium">Erreur de chargement</p>
          <p className="text-sm text-muted-foreground mt-1">{error instanceof Error ? error.message : 'Erreur inconnue'}</p>
        </div>
      </PageShell>
    );
  }

  if (isLoading || !summary) {
    return (
      <PageShell device={device} title="Dashboard" breadcrumb="Principal">
        <div className="p-8 text-muted-foreground">Chargement…</div>
      </PageShell>
    );
  }

  const latestAt = summary.obs.latest_at
    ? new Date(summary.obs.latest_at).toLocaleString('fr-FR')
    : '—';

  return (
    <PageShell device={device} title="Dashboard" breadcrumb="Principal">
      <div className={`p-4 md:p-6 space-y-4 ${device === 'mobile' ? '' : 'max-w-6xl'}`}>

        {/* ─── Compteurs globaux ─── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Postes total" value={summary.postes.total} />
          <Stat label="Incidents actifs" value={summary.incidents.active} sub={`${summary.incidents.last_24h} sur 24h`} />
          <Stat label="Obs / minute" value={summary.obs.last_minute_count} />
          <Stat label="Dernière obs" value={latestAt} sub="UTC" />
        </div>

        {/* ─── Data sources breakdown ─── */}
        {Object.keys(summary.postes.by_data_source).length > 0 && (
          <section className="bg-card border border-border rounded-lg p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Postes par source</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(summary.postes.by_data_source).map(([ds, count]) => (
                <Stat key={ds} label={ds} value={count} />
              ))}
            </div>
          </section>
        )}

        {/* ─── Navigation rapide ─── */}
        <section className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Navigation</h2>
          <div className="flex flex-wrap gap-2">
            <NavLink to="/postes" label="Postes" />
            <NavLink to="/db" label="Base de données" />
            <NavLink to="/incidents" label="Incidents" badge={summary.incidents.active > 0 ? String(summary.incidents.active) : undefined} />
            <NavLink to="/ingest" label="Ingestion" />
            <NavLink to="/jobs" label="Jobs" />
          </div>
        </section>

        {/* ─── Services (statique v1) ─── */}
        <section className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Services
            <span className="ml-2 text-[10px] font-normal text-muted-foreground">(statut live — v2)</span>
          </h2>
          <div className="space-y-2 text-sm">
            <ServiceRow name="Ingestion (ingestion_climato)" status="unknown" url="ingest.meteor-oi.re" />
            <ServiceRow name="Réception JSON (reception_json)" status="unknown" url="climato-json.meteor-oi.re" />
            <ServiceRow name="Traitement MQTT (traitement_mqtt)" status="unknown" url="mqtt.meteor-oi.re" />
          </div>
        </section>
      </div>
    </PageShell>
  );
}

function Stat({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="text-xs uppercase text-muted-foreground tracking-wider">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function NavLink({ to, label, badge }: { to: string; label: string; badge?: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border hover:bg-accent text-sm font-medium"
    >
      {label}
      {badge && (
        <span className="badge-amber px-1.5 py-0.5 rounded text-[10px] font-medium">{badge}</span>
      )}
    </Link>
  );
}

function ServiceRow({ name, status, url }: { name: string; status: 'up' | 'down' | 'unknown'; url: string }) {
  const dot = status === 'up' ? 'bg-green-500' : status === 'down' ? 'bg-red-500' : 'bg-slate-400';
  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        <span className="font-medium">{name}</span>
      </div>
      <span className="text-xs font-mono text-muted-foreground">{url}</span>
    </div>
  );
}
